// rollback-execution.ts
//
// Split off rollback-coordinator.ts to stay ≤200 lines (test 48-49): running
// an approved rollback recipe (honest receipt even on failure — never claims
// "base untouched") and finalizing ABORTED only after consequences are
// handled AND a receipt is written.
import { randomUUID } from "node:crypto";
import type { MissionStore } from "./mission-store.js";

export interface RunRollbackInput {
  store: MissionStore;
  missionId: string;
  authorityRef: string;
  runRollbackRecipe: () => Promise<{ ok: boolean; detail: string }>;
}

export type RollbackOutcome =
  | { status: "rolled_back"; operationId: string }
  | { status: "rollback_failed"; operationId: string; reason: string };

/**
 * Test 48: a failed rollback NEVER claims "base untouched" — the receipt
 * records exactly what the recipe reported and routes to WAITING_HUMAN via
 * the caller (mission-state-machine owns that transition). This function's
 * job stops at journaling the true outcome, honestly.
 */
export async function runApprovedRollback(input: RunRollbackInput): Promise<RollbackOutcome> {
  const operationId = randomUUID();
  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "rollback",
        params: { mission_id: input.missionId, authority_ref: input.authorityRef },
      },
    },
  ]);

  const outcome = await input.runRollbackRecipe();

  await input.store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome: outcome.ok ? "rolled_back" : "rollback_failed",
      },
    },
  ]);

  if (!outcome.ok) {
    return {
      status: "rollback_failed",
      operationId,
      reason: `Rollback recipe báo thất bại: ${outcome.detail}. KHÔNG coi base là untouched — cần người xác nhận trạng thái thật.`,
    };
  }
  return { status: "rolled_back", operationId };
}

export interface FinalizeAbortedInput {
  store: MissionStore;
  missionId: string;
  /** Bằng chứng hệ quả đã xử lý xong: rollback thành công, HOẶC xác nhận chưa từng deploy. */
  consequencesHandled: { kind: "rolled_back" | "no_deploy_occurred"; operationId?: string };
}

/**
 * Test 49: ABORTED chỉ đạt được SAU KHI hệ quả đã xử lý (rollback thành
 * công, hoặc xác nhận rõ ràng chưa từng có side effect nào) VÀ receipt được
 * ghi ở đây — không có đường tắt nào khác đạt ABORTED.
 */
export async function finalizeAborted(
  input: FinalizeAbortedInput,
): Promise<{ operationId: string }> {
  const operationId = randomUUID();
  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "abort_finalize",
        params: { mission_id: input.missionId, consequences: input.consequencesHandled },
      },
    },
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome: "aborted",
      },
    },
  ]);
  return { operationId };
}
